import { useState } from 'react';

import {
  formatGrams,
  formatWeight,
  isAmountEmpty,
  nutritionSummaryLine,
  type NutritionAmount,
} from '../core/nutrition.ts';
import { useLang, useStrings } from '../i18n/index.ts';
import { Icon } from './icons.tsx';

/** Четыре ячейки пищевой ценности: ккал, белки, жиры, углеводы; неизвестное — прочерк. */
export function NutritionCells({ amount }: { amount: NutritionAmount }) {
  const strings = useStrings();
  const lang = useLang();
  const cell = (value: string | null, label: string) => (
    <div className="nutrition-cell">
      <b className="t-numeric">{value ?? '–'}</b>
      <span>{label}</span>
    </div>
  );
  return (
    <div className="nutrition-cells">
      {cell(amount.kcal === null ? null : String(Math.round(amount.kcal)), strings.kcalUnit)}
      {cell(amount.proteinG === null ? null : formatGrams(amount.proteinG, lang), strings.proteinLabel)}
      {cell(amount.fatG === null ? null : formatGrams(amount.fatG, lang), strings.fatLabel)}
      {cell(amount.carbsG === null ? null : formatGrams(amount.carbsG, lang), strings.carbsLabel)}
    </div>
  );
}

/**
 * Вес, ккал и БЖУ корзины или заказа: приглушённая строка, которая по тапу разворачивается
 * в четыре ячейки карточки товара с полными подписями.
 */
export function NutritionSummary({ amount }: { amount: NutritionAmount }) {
  const strings = useStrings();
  const lang = useLang();
  const [expanded, setExpanded] = useState(false);
  if (isAmountEmpty(amount)) return null;

  // Развёрнутый заголовок: название блока и вес нетто; «≈» отмечает итог со строками без данных.
  let header = strings.nutrition;
  if (amount.weightG > 0) header += ` · ${formatWeight(amount.weightG, lang)}`;
  if (amount.isPartial) header = `≈ ${header}`;

  return (
    <div className="nutrition-summary">
      <button type="button" className="nutrition-summary-line" aria-expanded={expanded} aria-label={strings.nutrition} onClick={() => setExpanded(!expanded)}>
        <span className="grow">{expanded ? header : nutritionSummaryLine(amount, lang, strings)}</span>
        <Icon name="chevronDown" size={12} weight={2.8} className={expanded ? 'is-flipped' : undefined} />
      </button>
      {expanded && (
        <>
          <NutritionCells amount={amount} />
          {amount.isPartial && <p className="nutrition-partial">{strings.nutritionPartial}</p>}
        </>
      )}
    </div>
  );
}
